// client/src/components/GradientButton.js
// Shared gradient CTA button. Extracted from the identical inline-style +
// onMouseOver/onMouseOut blocks that were copy-pasted across the signup flow,
// Login, Contact, ForgotPassword and the static pages.
//
// IMPORTANT: every variant keeps the EXACT inline-style values of the
// original buttons — pixel parity matters more than elegance here. Do not
// "clean up" the numbers without checking every page that uses the variant.
//
// Variants:
//   green       - lime signup-flow button (SignupStepOne/Two/Three)
//   blue        - standard "Sign Up Now / Back to Home" CTA
//                 (HomePage, HowItWorks, Terms, Privacy, FAQs)
//   blueCompact - smaller-lift blue form button (Login, SignupSuccess)
//   emerald     - green form-submit button (ForgotPassword, Contact);
//                 layout (padding/fontSize/width/transition) is passed
//                 per-instance via the `style` prop
//
// Props: variant, type (default "button"), onClick, disabled, style
// (merged over the variant base), hoverStyle / restStyle (merged over the
// variant hover/rest values), children, plus any other button props.
// Hover effects are skipped while `disabled` is true, matching the original
// `if (!isValidating) { ... }`-style guards.
import React from 'react';

const VARIANTS = {
  green: {
    base: {
      padding: '1rem 2rem',
      background: 'linear-gradient(135deg, #A6CE3A 0%, #8BB62A 100%)',
      color: '#FFF',
      fontWeight: '700',
      fontSize: 'clamp(1rem, 4vw, 1.25rem)',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      boxShadow: '0 8px 25px rgba(166, 206, 58, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.3s ease',
      minHeight: '48px',
      minWidth: '120px',
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      letterSpacing: '0.5px',
      position: 'relative',
      overflow: 'hidden'
    },
    hover: {
      transform: 'translateY(-3px) scale(1.02)',
      boxShadow: '0 12px 35px rgba(166, 206, 58, 0.5), 0 6px 18px rgba(0, 0, 0, 0.2)',
      background: 'linear-gradient(135deg, #8BB62A 0%, #7AA228 100%)'
    },
    rest: {
      transform: 'translateY(0) scale(1)',
      boxShadow: '0 8px 25px rgba(166, 206, 58, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)',
      background: 'linear-gradient(135deg, #A6CE3A 0%, #8BB62A 100%)'
    }
  },
  blue: {
    base: {
      padding: '1rem 2rem',
      background: 'linear-gradient(135deg, #1E9FF2 0%, #14B1E7 100%)',
      color: '#FFF',
      fontWeight: '700',
      fontSize: 'clamp(1rem, 4vw, 1.25rem)',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      boxShadow: '0 8px 25px rgba(30, 159, 242, 0.5), 0 4px 12px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.3s ease',
      minHeight: '48px',
      minWidth: '200px',
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      letterSpacing: '0.5px',
      position: 'relative',
      overflow: 'hidden'
    },
    hover: {
      transform: 'translateY(-3px) scale(1.02)',
      boxShadow: '0 12px 35px rgba(30, 159, 242, 0.6), 0 6px 18px rgba(0, 0, 0, 0.2)',
      background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)'
    },
    rest: {
      transform: 'translateY(0) scale(1)',
      boxShadow: '0 8px 25px rgba(30, 159, 242, 0.5), 0 4px 12px rgba(0, 0, 0, 0.15)',
      background: 'linear-gradient(135deg, #1E9FF2 0%, #14B1E7 100%)'
    }
  },
  blueCompact: {
    base: {
      padding: '1rem 2rem',
      background: 'linear-gradient(135deg, #1E9FF2 0%, #14B1E7 100%)',
      color: '#FFF',
      fontWeight: '700',
      fontSize: '1.1rem',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      boxShadow: '0 8px 25px rgba(30, 159, 242, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)',
      transition: 'all 0.3s ease',
      minHeight: '48px',
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      letterSpacing: '0.5px',
      marginTop: '0.25rem'
    },
    hover: {
      transform: 'translateY(-2px) scale(1.01)',
      boxShadow: '0 12px 35px rgba(30, 159, 242, 0.5), 0 6px 18px rgba(0, 0, 0, 0.2)',
      background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)'
    },
    rest: {
      transform: 'translateY(0) scale(1)',
      boxShadow: '0 8px 25px rgba(30, 159, 242, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)',
      background: 'linear-gradient(135deg, #1E9FF2 0%, #14B1E7 100%)'
    }
  },
  emerald: {
    base: {
      background: 'linear-gradient(135deg, #27AE60 0%, #1E8E50 100%)',
      color: '#FFF',
      fontWeight: '700',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      boxShadow: '0 8px 25px rgba(39, 174, 96, 0.5), 0 4px 12px rgba(0, 0, 0, 0.15)',
      minHeight: '48px',
      textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      letterSpacing: '0.5px'
    },
    hover: {
      transform: 'translateY(-3px) scale(1.02)',
      boxShadow: '0 12px 35px rgba(39, 174, 96, 0.6), 0 6px 18px rgba(0, 0, 0, 0.2)',
      background: 'linear-gradient(135deg, #1E8E50 0%, #146B3A 100%)'
    },
    rest: {
      transform: 'translateY(0) scale(1)',
      boxShadow: '0 8px 25px rgba(39, 174, 96, 0.5), 0 4px 12px rgba(0, 0, 0, 0.15)',
      background: 'linear-gradient(135deg, #27AE60 0%, #1E8E50 100%)'
    }
  }
};

const applyState = (el, state) => {
  el.style.transform = state.transform;
  el.style.boxShadow = state.boxShadow;
  el.style.background = state.background;
};

const GradientButton = ({
  variant = 'green',
  type = 'button',
  onClick,
  disabled = false,
  style,
  hoverStyle,
  restStyle,
  children,
  ...rest
}) => {
  const v = VARIANTS[variant] || VARIANTS.green;
  const hover = hoverStyle ? { ...v.hover, ...hoverStyle } : v.hover;
  const out = restStyle ? { ...v.rest, ...restStyle } : v.rest;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={style ? { ...v.base, ...style } : v.base}
      onMouseOver={(e) => {
        if (!disabled) applyState(e.target, hover);
      }}
      onMouseOut={(e) => {
        if (!disabled) applyState(e.target, out);
      }}
      {...rest}
    >
      {children}
    </button>
  );
};

export default GradientButton;
